import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { auth, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail } from '../firebase';
import axios from 'axios';

export default function Auth({ onLogin }) {
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [gdprConsent, setGdprConsent] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();
    
    // Check GDPR consent
    if (!gdprConsent) {
      toast.error('You must agree to the Terms of Use and Privacy Policy to sign up.');
      return;
    }
    
    setIsLoading(true);

    const formData = new FormData(e.target);
    const email = formData.get('email');
    const password = formData.get('password');
    const name = formData.get('name');
    const unitPreference = formData.get('unit_preference') || 'metric';

    try {
      // Create user in Firebase
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Send email verification
      await sendEmailVerification(userCredential.user);
      toast.success('Verification email sent! Please check your inbox.');

      // Get Firebase ID token
      const idToken = await userCredential.user.getIdToken();

      // Create user profile in backend with GDPR consent
      const response = await axios.post('/auth/firebase-signup', {
        firebase_uid: userCredential.user.uid,
        email: email,
        name: name,
        unit_preference: unitPreference,
        gdpr_consent: true
      }, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });

      // DO NOT log user in automatically - they must verify email first
      toast.success('Account created! Please check your email and click the verification link to log in.');
      // Sign out from Firebase until they verify
      await auth.signOut();
    } catch (error) {
      console.error('Signup error:', error);
      if (error.code === 'auth/email-already-in-use') {
        toast.error('Email already in use');
      } else if (error.code === 'auth/weak-password') {
        toast.error('Password should be at least 6 characters');
      } else if (error.code === 'auth/network-request-failed') {
        toast.error('Network error. Please check your connection and try again.');
      } else if (error.message) {
        toast.error(`Signup failed: ${error.message}`);
      } else {
        toast.error('Signup failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.target);
    const email = formData.get('email');
    const password = formData.get('password');

    try {
      // First, try to login with our JWT backend (for existing users)
      try {
        const response = await axios.post('/auth/login', { email, password });
        toast.success('Welcome back!');
        onLogin(response.data.access_token, response.data.user);
        return;
      } catch (jwtError) {
        // If JWT login fails with 401, try Firebase
        if (jwtError.response?.status !== 401) {
          throw jwtError;
        }
      }

      // Try Firebase login for newer accounts
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Check if email is verified - MANDATORY
      if (!userCredential.user.emailVerified) {
        setUnverifiedEmail(email);
        setShowResendVerification(true);
        toast.error('Please verify your email before logging in.');
        await auth.signOut();
        setIsLoading(false);
        return;
      }

      // Get Firebase ID token
      const idToken = await userCredential.user.getIdToken();

      // Get user profile from backend
      const response = await axios.post('/auth/firebase-login', {
        firebase_uid: userCredential.user.uid
      }, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });

      toast.success('Welcome back!');
      onLogin(response.data.access_token, response.data.user);
    } catch (error) {
      console.error('Login error:', error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
        toast.error('Invalid email or password');
      } else if (error.code === 'auth/network-request-failed') {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error(error.response?.data?.detail || 'Invalid email or password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.target);
    const email = formData.get('resend-email');
    const password = formData.get('resend-password');

    try {
      // Sign in to get the user object
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      if (userCredential.user.emailVerified) {
        toast.success('Your email is already verified! You can log in now.');
        setShowResendVerification(false);
        await auth.signOut();
      } else {
        await sendEmailVerification(userCredential.user);
        toast.success('Verification email sent! Please check your inbox.');
        await auth.signOut();
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
        toast.error('Invalid email or password');
      } else if (error.code === 'auth/too-many-requests') {
        toast.error('Too many attempts. Please wait a few minutes before trying again.');
      } else {
        toast.error('Failed to resend verification email');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.target);
    const email = formData.get('reset-email');

    try {
      // Configure action code settings to redirect to our custom reset page
      const actionCodeSettings = {
        url: `${window.location.origin}/reset-password`,
        handleCodeInApp: true,
      };
      
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      toast.success('Password reset email sent! Check your inbox and click the link to reset your password.');
      setShowForgotPassword(false);
    } catch (error) {
      console.error('Password reset error:', error);
      if (error.code === 'auth/user-not-found') {
        // For users who signed up before Firebase, show a different message
        toast.error('This email is not registered with Firebase. Please contact support to reset your password.');
      } else if (error.code === 'auth/network-request-failed') {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error('Failed to send reset email');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 auth-page">
        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img 
                src="/rydrlink-logo.jpg" 
                alt="RydrLink Logo" 
                className="h-16 w-auto"
              />
            </div>
            <h1 className="text-5xl font-bold text-white mb-2 tracking-tight">
              Rydr<span className="text-purple-400">Link</span>
            </h1>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Reset Password</CardTitle>
              <CardDescription>Enter your email to receive a password reset link</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label htmlFor="reset-email">Email</Label>
                  <Input
                    id="reset-email"
                    name="reset-email"
                    type="email"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1" disabled={isLoading}>
                    {isLoading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowForgotPassword(false)}
                  >
                    Back
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (showResendVerification) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 auth-page">
        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img 
                src="/rydrlink-logo.jpg" 
                alt="RydrLink Logo" 
                className="h-16 w-auto"
              />
            </div>
            <h1 className="text-5xl font-bold text-white mb-2 tracking-tight">
              Rydr<span className="text-purple-400">Link</span>
            </h1>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Verify Your Email</CardTitle>
              <CardDescription>Enter your credentials to resend the verification email</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleResendVerification} className="space-y-4">
                <div>
                  <Label htmlFor="resend-email">Email</Label>
                  <Input
                    id="resend-email"
                    name="resend-email"
                    type="email"
                    placeholder="your@email.com"
                    defaultValue={unverifiedEmail}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="resend-password">Password</Label>
                  <Input
                    id="resend-password"
                    name="resend-password"
                    type="password"
                    placeholder="••••••••"
                    required
                  />
                </div>
                <div className="bg-amber-500/10 border border-amber-500/20 p-3 rounded-md">
                  <p className="text-sm text-amber-200">
                    Check your inbox (and spam folder) for the verification email. Click the link to verify, then come back to log in.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1" disabled={isLoading}>
                    {isLoading ? 'Sending...' : 'Resend Verification'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowResendVerification(false)}
                  >
                    Back
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 auth-page">
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img 
              src="/rydrlink-logo.jpg" 
              alt="RydrLink Logo" 
              className="h-16 w-auto"
            />
          </div>
          <h1 className="text-5xl font-bold text-white mb-2 tracking-tight">
            Rydr<span className="text-purple-400">Link</span>
          </h1>
          <p className="text-gray-400 text-lg font-medium">Meet. Match. Ride.</p>
        </div>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
            <TabsTrigger value="signup" data-testid="signup-tab">Sign Up</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <Card>
              <CardHeader>
                <CardTitle>Welcome Back</CardTitle>
                <CardDescription>Login to find your next ride</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      required
                      data-testid="login-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      required
                      data-testid="login-password"
                    />
                  </div>
                  <div className="flex justify-between text-sm">
                    <button
                      type="button"
                      onClick={() => setShowResendVerification(true)}
                      className="text-purple-400 hover:text-purple-300 underline"
                    >
                      Resend verification?
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-purple-400 hover:text-purple-300 underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading} data-testid="login-submit">
                    {isLoading ? 'Logging in...' : 'Login'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="signup">
            <Card>
              <CardHeader>
                <CardTitle>Create Account</CardTitle>
                <CardDescription>Join the cycling community</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSignup} className="space-y-4">
                  <div>
                    <Label htmlFor="signup-name">Name</Label>
                    <Input
                      id="signup-name"
                      name="name"
                      placeholder="Your name"
                      required
                      data-testid="signup-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      required
                      data-testid="signup-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="signup-password">Password</Label>
                    <Input
                      id="signup-password"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      required
                      minLength={6}
                      data-testid="signup-password"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Minimum 6 characters
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="unit_preference">Preferred Units</Label>
                    <select
                      id="unit_preference"
                      name="unit_preference"
                      className="w-full p-2 rounded-md bg-gray-800 border border-gray-700 text-white"
                      data-testid="signup-units"
                      defaultValue="metric"
                    >
                      <option value="metric">Metric (km)</option>
                      <option value="imperial">Imperial (miles)</option>
                    </select>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="gdpr-consent"
                      checked={gdprConsent}
                      onCheckedChange={setGdprConsent}
                      data-testid="gdpr-consent"
                      className="mt-1"
                    />
                    <label htmlFor="gdpr-consent" className="text-sm text-gray-300 leading-tight cursor-pointer">
                      I agree to the{' '}
                      <Link to="/terms" className="text-purple-400 hover:text-purple-300 underline" target="_blank">
                        Terms of Use
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy" className="text-purple-400 hover:text-purple-300 underline" target="_blank">
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                  <div className="bg-accent p-3 rounded-md">
                    <p className="text-xs text-muted-foreground">
                      You'll receive a verification email after signing up
                    </p>
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading || !gdprConsent} data-testid="signup-submit">
                    {isLoading ? 'Creating account...' : 'Sign Up'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
